/**
 * ジョブ基盤 `/jobs`。
 *
 * タブは 5 つ:
 *   provisioning … プロビジョニングジョブ（メールボックス作成・DNS 検証・証明書）
 *   dead-letter  … 再試行を使い切って落ちたジョブ。再投入と破棄
 *   outbox       … transactional outbox（Redis へ積めていない行）
 *   workers      … ワーカープロセスの死活
 *   schedules    … Redis の繰り返し登録と定期ジョブの実行台帳
 *
 * **DLQ タブがいちばん重要**（A141）。`dead-letter` キューはコンシューマを
 * 持たないので、ジョブは実行されず `waiting` のまま積まれる。画面が無い間に
 * **63 件溜まっていた**ことがある（`notify/email.send` 718 件を横取りされた
 * 事故。CLAUDE.md §3）。ワーカー死活も同じ理由で外から見えないと意味が無い
 * （worker が死ぬと worker 自身の通報経路も死ぬ）。
 *
 * プロビジョニングの既定フィルタは「直近 24 時間」。既定で全期間を出すと、
 * 正常終了した何万件かに失敗の数件が埋もれる。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlarmClock,
  Inbox,
  Layers,
  ListChecks,
  MoreHorizontal,
  RotateCcw,
  Skull,
  Trash2,
  XCircle,
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  CursorPagination,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  Section,
  StatCard,
  StatusBadge,
  Textarea,
  formatCount,
  formatDateTime,
  formatRelative,
  type DataTableColumn,
} from '@mail-sv/ui';
import { JOB_KIND, JOB_STATUS, labelOf } from '@mail-sv/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { formatDuration } from '../lib/format.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { hasActiveFilter, sinceToIso } from '../lib/search.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import {
  JOB_SUBJECT_TYPE_LABEL,
  OUTBOX_STATUS_LABEL,
  OUTBOX_STATUS_TONE,
  labelFrom,
  optionsFromLabels,
  toneFrom,
} from '../lib/ops-labels.js';
import { usePermission } from '../stores/auth.js';
import { FilterBar, optionsFrom } from '../components/filter-bar.js';
import { PageTabs } from '../components/page-tabs.js';
import { CodeBlock } from '../components/code-block.js';
import { FormDialog } from '../components/dialogs.js';
import { Field } from '../components/form-fields.js';
import { ListEmpty, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type {
  CountedList,
  DeadLetterEntry,
  DeadLetterPage,
  DeadLetterStatus,
  JobSchedulerEntry,
  JobSummary,
  OutboxEntry,
  OutboxSummary,
  QueueDepth,
  ScheduledRunEntry,
  WorkerHeartbeatEntry,
  WorkerHeartbeatList,
} from '../types/api.js';

const routeApi = getRouteApi(RID.jobs);

/** これ以上進まない状態。ポーリング停止の判定に使う。 */
const TERMINAL: ReadonlySet<string> = new Set(['succeeded', 'failed', 'canceled', 'compensated', 'skipped']);

/**
 * プロビジョニングタブで「絞り込み中」と見なさない search key。
 * `tab` / `outboxStatus` は他タブの状態なので、これを数えると
 * 開いた瞬間から「フィルタをクリア」が出続ける。
 */
const PROVISIONING_IGNORE = ['tab', 'range', 'since', 'sort', 'order', 'outboxStatus'];

export function JobsPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  return (
    <>
      <PageHeader
        title="ジョブ"
        description="非同期処理の実行状況と、失敗したジョブ・積めていないジョブ・ワーカーの死活。"
      />

      <PageTabs
        value={search.tab}
        onChange={(tab) => void navigate({ search: (prev) => ({ ...prev, tab }) })}
        tabs={[
          { value: 'provisioning', label: 'プロビジョニング', icon: ListChecks },
          { value: 'dead-letter', label: 'デッドレター', icon: Skull },
          { value: 'outbox', label: 'Outbox', icon: Inbox },
          { value: 'workers', label: 'ワーカー', icon: Activity },
          { value: 'schedules', label: '定期実行', icon: AlarmClock },
        ]}
      />

      {search.tab === 'provisioning' ? <ProvisioningTab /> : null}
      {search.tab === 'dead-letter' ? <DeadLetterTab /> : null}
      {search.tab === 'outbox' ? <OutboxTab /> : null}
      {search.tab === 'workers' ? <WorkersTab /> : null}
      {search.tab === 'schedules' ? <SchedulesTab /> : null}
    </>
  );
}

/* --------------------------------------------------- プロビジョニング */

function ProvisioningTab(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canCommand = usePermission(OPS_PERMISSION.nodeCommand);

  const [hasRunning, setHasRunning] = React.useState(false);

  const list = useCursorList<JobSummary>({
    path: '/jobs/provisioning',
    keyFor: (params) => qk.jobs(params),
    filters: {
      q: search.q,
      status: search.status,
      kind: search.kind,
      organizationId: search.organizationId,
      // 対象種別 / 対象 ID（R81）。1 件のメールボックスに関する履歴を追うための軸。
      subjectType: search.subjectType,
      subjectId: search.subjectId,
      from: sinceToIso(search.since),
    },
    refetchInterval: hasRunning ? 3_000 : false,
  });

  // 行の状態から次のポーリング要否を決める。render 中に setState しないよう effect で。
  React.useEffect(() => {
    setHasRunning(list.rows.some((j) => !TERMINAL.has(j.status)));
  }, [list.rows]);

  const setSearch = (patch: Partial<typeof search>): void => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  };

  const columns: Array<DataTableColumn<JobSummary>> = [
    {
      id: 'status',
      header: '状態',
      width: 'w-28',
      cell: (j) => <StatusBadge status={j.status} size="sm" dot pulse={j.status === 'running'} />,
    },
    {
      id: 'kind',
      header: '種別',
      cell: (j) => (
        <Link to="/jobs/$jobId" params={{ jobId: j.id }} className="font-medium hover:text-primary">
          {labelOf(j.kind)}
        </Link>
      ),
    },
    {
      id: 'target',
      header: '対象',
      /*
       * **クリックで「この対象だけ」に絞る**（R81）。ID を選択してコピーし、
       * フィルタ欄へ貼り直す往復が、障害調査でいちばん多い操作だった。
       * 行クリックの遷移（ジョブ詳細）とは別の動作なので、
       * `stopPropagation` して行の `onRowClick` を発火させない
       * ……のだが、この表は行クリックを使っていない（種別列がリンク）ので
       * 実害は無い。将来行クリックを付けたときの事故を先に潰しておく。
       */
      cell: (j) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setSearch({ subjectType: j.subjectType, subjectId: j.subjectId ?? undefined });
          }}
          className="rounded text-left text-sm text-muted-foreground hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          title="この対象のジョブだけを表示する"
        >
          {j.subjectId
            ? `${labelFrom(JOB_SUBJECT_TYPE_LABEL, j.subjectType)} / ${j.subjectId}`
            : labelFrom(JOB_SUBJECT_TYPE_LABEL, j.subjectType)}
        </button>
      ),
    },
    {
      id: 'org',
      header: '契約組織',
      hideBelow: 'md',
      cell: (j) =>
        j.organizationId ? (
          <Link
            to="/organizations/$orgId"
            params={{ orgId: j.organizationId }}
            search={{ tab: 'overview' }}
            className="hover:text-primary"
          >
            {j.organizationName ?? j.organizationId}
          </Link>
        ) : (
          '—'
        ),
    },
    { id: 'startedAt', header: '開始', hideBelow: 'md', cell: (j) => formatDateTime(j.startedAt) },
    { id: 'duration', header: '所要', align: 'right', hideBelow: 'lg', cell: (j) => formatDuration(j.durationMs) },
    {
      id: 'attempt',
      header: '試行',
      align: 'right',
      hideBelow: 'lg',
      cell: (j) => (j.attempts > 1 ? <Badge variant="warning" size="sm">{j.attempts}</Badge> : j.attempts),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      width: 'w-12',
      align: 'right',
      cell: (j) => <JobRowActions job={j} canCommand={canCommand} />,
    },
  ];

  return (
    <>
      <QueueSummary />

      <FilterBar
        q={search.q}
        onQChange={(q) => setSearch({ q })}
        placeholder="対象 ID で絞り込む"
        active={hasActiveFilter(search, PROVISIONING_IGNORE)}
        onClear={() => void navigate({ search: { tab: 'provisioning', since: '24h', outboxStatus: 'pending' } })}
        selects={[
          {
            key: 'status',
            label: '状態',
            value: search.status,
            options: optionsFrom(JOB_STATUS, labelOf),
            onChange: (v) => setSearch({ status: v as typeof search.status }),
          },
          {
            key: 'kind',
            label: '種別',
            value: search.kind,
            options: optionsFrom(JOB_KIND, labelOf),
            onChange: (v) => setSearch({ kind: v as typeof search.kind }),
          },
          {
            key: 'since',
            label: '期間',
            value: search.since,
            allLabel: '24 時間',
            options: [
              { value: '1h', label: '1 時間' },
              { value: '24h', label: '24 時間' },
              { value: '7d', label: '7 日' },
              { value: '30d', label: '30 日' },
            ],
            onChange: (v) => setSearch({ since: (v ?? '24h') as typeof search.since }),
          },
          {
            key: 'subjectType',
            label: '対象種別',
            value: search.subjectType,
            options: optionsFromLabels(JOB_SUBJECT_TYPE_LABEL),
            onChange: (v) => setSearch({ subjectType: v }),
          },
        ]}
      >
        {/*
          対象 ID は完全一致（キーワード欄の <code className="font-mono">q</code> は部分一致）。
          「この 1 件だけ」を追うときはこちら、探すときは <code className="font-mono">q</code> を使う。
          行の「対象」列からワンクリックで入る導線も用意してある。
        */}
        <div className="w-52">
          <Input
            value={search.subjectId ?? ''}
            onChange={(e) => setSearch({ subjectId: e.target.value || undefined })}
            placeholder="対象 ID（完全一致）"
            aria-label="対象 ID で絞り込む"
            className="h-9 font-mono text-xs"
          />
        </div>
        <div className="ml-auto">
          <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
        </div>
      </FilterBar>

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(j) => j.id}
        loading={list.query.isLoading}
        empty={
          <ListEmpty
            filtered={hasActiveFilter(search, PROVISIONING_IGNORE)}
            entityLabel="ジョブ"
            onClearFilter={() =>
              void navigate({ search: { tab: 'provisioning', since: '24h', outboxStatus: 'pending' } })
            }
          />
        }
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />
    </>
  );
}

/* ------------------------------------------------------------- キュー概況 */

function QueueSummary(): React.ReactElement | null {
  const query = useQuery({
    queryKey: qk.queues(),
    queryFn: () => api.get<CountedList<QueueDepth>>('/jobs/queues'),
    refetchInterval: 10_000,
  });

  // API は `{items:[{queue,counts}],total}`。滞留のあるキューだけ出す。
  const queues = (query.data?.items ?? []).filter(
    (q) => q.counts.waiting + q.counts.active + q.counts.failed + q.counts.delayed > 0,
  );
  if (queues.length === 0) return null;

  return (
    <Section title="キューの滞留" className="mb-5">
      <div className="flex flex-wrap gap-2">
        {queues.map((q) => (
          <div
            key={q.queue}
            className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-xs"
          >
            <span className="font-mono font-medium">{q.queue}</span>
            <span className="tabular text-muted-foreground">待機 {formatCount(q.counts.waiting)}</span>
            <span className="tabular text-info">実行 {formatCount(q.counts.active)}</span>
            {q.counts.failed > 0 ? (
              <span className="tabular text-destructive">失敗 {formatCount(q.counts.failed)}</span>
            ) : null}
            {q.counts.paused > 0 ? <Badge variant="warning" size="sm">停止中</Badge> : null}
          </div>
        ))}
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------- 行アクション */

function JobRowActions({ job, canCommand }: { job: JobSummary; canCommand: boolean }): React.ReactElement {
  const qc = useQueryClient();
  const retry = useMutation({
    mutationFn: () => api.post(`/jobs/provisioning/${job.id}/retry`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.jobs() }),
  });
  const cancel = useMutation({
    mutationFn: () => api.post(`/jobs/provisioning/${job.id}/cancel`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.jobs() }),
  });

  const cancellable = job.status === 'queued' || job.status === 'running';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label="ジョブの操作">
          <MoreHorizontal aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem asChild>
          <Link to="/jobs/$jobId" params={{ jobId: job.id }}>
            詳細を開く
          </Link>
        </DropdownMenuItem>
        {canCommand ? (
          <>
            <DropdownMenuItem onSelect={() => retry.mutate()} disabled={retry.isPending}>
              <RotateCcw className="size-4" aria-hidden />
              再実行
            </DropdownMenuItem>
            {cancellable ? (
              <DropdownMenuItem
                className="text-destructive"
                onSelect={() => cancel.mutate()}
                disabled={cancel.isPending}
              >
                <XCircle className="size-4" aria-hidden />
                キャンセル
              </DropdownMenuItem>
            ) : null}
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ---------------------------------------------------------- デッドレター */

/**
 * デッドレターの一覧と再投入 / 破棄。
 *
 * **`dead-letter` キューにはコンシューマが居ない**ので、転送されたジョブは
 * `waiting` のまま積まれる。API 側も `waiting`/`delayed`/`failed`/`paused` を
 * 見るようになっている（`failed` だけを見ていた頃は構造上必ず空だった）。
 *
 * 再投入は元のキュー・元のジョブ名・元の jobId でやり直す。破棄は理由必須。
 */
function DeadLetterTab(): React.ReactElement {
  const canWrite = usePermission(OPS_PERMISSION.jobWrite);
  const [offset, setOffset] = React.useState(0);
  const [limit] = React.useState(50);
  const [expanded, setExpanded] = React.useState<string | null>(null);
  const [discarding, setDiscarding] = React.useState<DeadLetterEntry | null>(null);

  const query = useQuery({
    queryKey: qk.deadLetter({ offset, limit }),
    // cursor ではなく offset ページング（BullMQ の getJobs がそういう API）。
    queryFn: () => api.get<DeadLetterPage>('/jobs/dead-letter', { offset, limit }),
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });

  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const status = query.data?.status ?? null;

  const columns: Array<DataTableColumn<DeadLetterEntry>> = [
    {
      id: 'originJobName',
      header: 'ジョブ',
      cell: (d) => (
        <div className="min-w-0 space-y-0.5">
          <span className="block truncate font-mono text-sm font-medium">{d.originJobName || '（不明）'}</span>
          <span className="block truncate text-xs text-muted-foreground">{d.originQueue || '（不明）'}</span>
        </div>
      ),
    },
    {
      id: 'failedReason',
      header: '失敗理由',
      cell: (d) => (
        <span className="line-clamp-2 text-xs text-destructive" title={d.failedReason}>
          {d.failedReason || '—'}
        </span>
      ),
    },
    { id: 'attempts', header: '試行', align: 'right', hideBelow: 'md', cell: (d) => d.attemptsMade },
    {
      id: 'failedAt',
      header: '失敗時刻',
      hideBelow: 'md',
      cell: (d) => formatDateTime(d.failedAt ?? new Date(d.timestamp).toISOString()),
    },
    {
      id: 'payload',
      header: <span className="sr-only">ペイロード</span>,
      align: 'right',
      width: 'w-28',
      cell: (d) => (
        <Button variant="ghost" size="sm" onClick={() => setExpanded((p) => (p === d.id ? null : d.id))}>
          {expanded === d.id ? '閉じる' : 'ペイロード'}
        </Button>
      ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-32',
      cell: (d) => (canWrite ? <DeadLetterActions entry={d} onDiscard={() => setDiscarding(d)} /> : null),
    },
  ];

  const opened = rows.find((d) => d.id === expanded) ?? null;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          label="デッドレター"
          value={formatCount(total)}
          icon={Skull}
          tone={total > 0 ? 'destructive' : 'default'}
          hint="再試行を使い切って捨てられたジョブ。放置すると通知メールなどが黙って消えます。"
        />
        {/*
          **抑止した件数を必ず並べて出す。** 一覧は原因ごとに数件へ畳まれるので、
          「5 件しか失敗していない」に見えるが実際は数十万件かもしれない
          （2026-09-06 に本番で 404,019 件・1.9 GiB が溜まった）。
        */}
        <StatCard
          label="抑止（重複）"
          value={formatCount(status?.suppressed ?? 0)}
          icon={Layers}
          tone={(status?.suppressed ?? 0) > 0 ? 'warning' : 'default'}
          hint="同じ原因の繰り返しは数件だけ残して畳んでいます。この数が実際の失敗回数です。"
        />
        <StatCard label="表示中" value={formatCount(rows.length)} hint={`${offset + 1} 件目から`} />
      </div>

      <SuppressedCauses status={status} />

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(d) => d.id}
        loading={query.isLoading}
        selectedKey={expanded}
        empty={<ListEmpty filtered={false} entityLabel="デッドレター" />}
        footer={
          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              全 {formatCount(total)} 件中 {rows.length > 0 ? offset + 1 : 0}–{offset + rows.length} 件
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0 || query.isFetching}
                onClick={() => setOffset((o) => Math.max(0, o - limit))}
              >
                前へ
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={offset + rows.length >= total || query.isFetching}
                onClick={() => setOffset((o) => o + limit)}
              >
                次へ
              </Button>
            </div>
          </div>
        }
      />

      {opened ? (
        <div className="space-y-1.5">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            元ジョブのペイロード（{opened.originJobId ?? 'jobId なし'}）
          </p>
          <CodeBlock value={JSON.stringify(opened.data, null, 2)} language="json" />
        </div>
      ) : null}

      <DiscardDialog entry={discarding} onClose={() => setDiscarding(null)} />
    </div>
  );
}

/**
 * 抑止された原因の内訳。
 *
 * **一覧に出ていない失敗を見せるための唯一の場所。** 入口の門
 * （`apps/worker/src/lib/dead-letter-guard.ts`）は同じ原因を数件で打ち切るので、
 * 「一覧が 5 件」＝「5 回しか失敗していない」ではない。ここを出さないと、
 * 抑止は「静かに壊れている」のと区別が付かなくなる。
 *
 * `checkedAt` が古い＝ `sweep.dead-letter` 自体が止まっているので、
 * **数字より先に鮮度を出す**（古い数字を現在の状態として読ませない）。
 */
function SuppressedCauses({ status }: { status: DeadLetterStatus | null }): React.ReactElement | null {
  if (!status) return null;

  const checkedAt = new Date(status.checkedAt);
  const staleMs = Date.now() - checkedAt.getTime();
  // sweep は 30 分周期。2 周期ぶん過ぎたら「止まっている」と扱う。
  const stale = Number.isFinite(staleMs) && staleMs > 90 * 60_000;

  if (status.suppressed === 0 && !stale) return null;

  return (
    <Alert variant={stale ? 'destructive' : 'warning'}>
      <Layers className="size-4" />
      <AlertTitle>
        {stale
          ? `集計が ${formatRelative(status.checkedAt)}のまま更新されていません`
          : `同じ原因の重複を ${formatCount(status.suppressed)} 件抑止しています（原因 ${status.suppressedCauses} 種類）`}
      </AlertTitle>
      <AlertDescription className="space-y-2">
        {stale ? (
          <p>
            <code>sweep.dead-letter</code> が止まっている可能性があります。下の数字は
            {formatRelative(status.checkedAt)}時点のもので、現在の状態ではありません。
            「ワーカー」タブで死活を確認してください。
          </p>
        ) : (
          <p>
            一覧には原因ごとに数件だけを残しています。<strong>実際の失敗回数はこの数です。</strong>
            （集計: {formatDateTime(status.checkedAt)}）
          </p>
        )}
        {status.topCauses.length > 0 ? (
          <ul className="space-y-1.5">
            {status.topCauses.map((c) => (
              <li key={c.fingerprint} className="min-w-0">
                <span className="font-mono text-xs font-medium">
                  {c.queue}/{c.jobName}
                </span>
                <Badge variant="outline" className="ml-2">
                  {formatCount(c.count)} 件
                </Badge>
                <span className="ml-2 text-xs text-muted-foreground">
                  {c.reason === 'capacity' ? '（全体の上限）' : '（重複）'}
                </span>
                <span className="block truncate text-xs text-muted-foreground" title={c.sample}>
                  {c.sample}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

function DeadLetterActions({
  entry,
  onDiscard,
}: {
  entry: DeadLetterEntry;
  onDiscard: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const retry = useMutation({
    mutationFn: () => api.post(`/jobs/dead-letter/${entry.id}/retry`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ops', 'dead-letter'] }),
  });

  // 元のキュー / ジョブ名が記録されていないものは API 側で 400 になる。
  // 押せるのに必ず失敗するボタンを出さない。
  const retryable = entry.originQueue.length > 0 && entry.originJobName.length > 0;

  return (
    <div className="flex justify-end gap-1">
      <Button
        variant="outline"
        size="sm"
        disabled={!retryable}
        title={retryable ? undefined : '元のキュー / ジョブ名が記録されていないため再投入できません'}
        loading={retry.isPending}
        onClick={() => retry.mutate()}
      >
        <RotateCcw aria-hidden />
        再投入
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="破棄する"
        className="text-destructive"
        onClick={onDiscard}
      >
        <Trash2 aria-hidden />
      </Button>
    </div>
  );
}

function DiscardDialog({
  entry,
  onClose,
}: {
  entry: DeadLetterEntry | null;
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (entry) setReason('');
  }, [entry]);

  const discard = useMutation({
    mutationFn: () =>
      api.post(`/jobs/dead-letter/${entry?.id ?? ''}/discard`, { reason: reason.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'dead-letter'] });
      onClose();
    },
  });

  return (
    <FormDialog
      open={entry !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="デッドレターを破棄する"
      description={entry ? `${entry.originQueue} / ${entry.originJobName}` : undefined}
      submitLabel="破棄する"
      pending={discard.isPending}
      error={discard.error}
      disabled={reason.trim().length === 0}
      onSubmit={() => discard.mutate()}
    >
      <Alert variant="warning">
        <AlertDescription>
          破棄すると元に戻せません。「対応不要」と判断した根拠を残してください（監査ログに載ります）。
        </AlertDescription>
      </Alert>
      <Field label="破棄する理由" required>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="廃止済みのジョブ名。再投入しても同じ理由で失敗するため。"
        />
      </Field>
    </FormDialog>
  );
}

/* --------------------------------------------------------------- Outbox */

/**
 * transactional outbox。
 *
 * **ここに行が溜まっている = Redis へ積めていない**、という一次情報。
 * 増え続けているときはまず Redis の疎通を疑う。`dead` は 10 回失敗して
 * 捨てられた行で、原因（廃止されたキュー名・Redis 断）を直したあと
 * 手で `pending` に戻す。
 */
function OutboxTab(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canWrite = usePermission(OPS_PERMISSION.jobWrite);

  const summary = useQuery({
    queryKey: qk.outboxSummary(),
    queryFn: () => api.get<OutboxSummary>('/jobs/outbox/summary'),
    refetchInterval: 30_000,
  });

  const list = useCursorList<OutboxEntry>({
    path: '/jobs/outbox',
    keyFor: (params) => qk.outbox(params),
    filters: { status: search.outboxStatus },
    refetchInterval: 30_000,
  });

  const counts = summary.data?.items ?? {};

  const columns: Array<DataTableColumn<OutboxEntry>> = [
    {
      id: 'status',
      header: '状態',
      width: 'w-36',
      cell: (o) => (
        <StatusBadge
          status={o.status}
          size="sm"
          dot
          tone={toneFrom(OUTBOX_STATUS_TONE, o.status)}
          label={labelFrom(OUTBOX_STATUS_LABEL, o.status)}
        />
      ),
    },
    {
      id: 'jobName',
      header: 'ジョブ',
      cell: (o) => (
        <div className="min-w-0 space-y-0.5">
          <span className="block truncate font-mono text-sm">{o.jobName}</span>
          <span className="block truncate text-xs text-muted-foreground">{o.queue}</span>
        </div>
      ),
    },
    { id: 'attempts', header: '試行', align: 'right', hideBelow: 'md', cell: (o) => o.attempts },
    {
      id: 'lastError',
      header: '直近のエラー',
      hideBelow: 'md',
      cell: (o) => (
        <span className="line-clamp-2 text-xs text-muted-foreground" title={o.lastError ?? undefined}>
          {o.lastError ?? '—'}
        </span>
      ),
    },
    { id: 'nextAttemptAt', header: '次の試行', hideBelow: 'lg', cell: (o) => formatRelative(o.nextAttemptAt) },
    { id: 'createdAt', header: '作成', hideBelow: 'lg', cell: (o) => formatDateTime(o.createdAt) },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-32',
      // `sent` は再投入できない（API が 400 を返す）。
      cell: (o) => (canWrite && o.status !== 'sent' ? <RequeueButton id={o.id} /> : null),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="未送出"
          value={formatCount(counts.pending ?? 0)}
          tone={(counts.pending ?? 0) > 0 ? 'warning' : 'default'}
        />
        <StatCard label="送出済み" value={formatCount(counts.sent ?? 0)} />
        <StatCard
          label="失敗（再試行中）"
          value={formatCount(counts.failed ?? 0)}
          tone={(counts.failed ?? 0) > 0 ? 'warning' : 'default'}
        />
        <StatCard
          label="打ち切り"
          value={formatCount(counts.dead ?? 0)}
          tone={(counts.dead ?? 0) > 0 ? 'destructive' : 'default'}
        />
      </div>

      {(counts.pending ?? 0) > 50 ? (
        <Alert variant="destructive">
          <AlertTitle>未送出が滞留しています</AlertTitle>
          <AlertDescription>
            outbox に行が溜まっているのは「Redis へ積めていない」ことの一次情報です。
            まず Redis の疎通を確認してください。
          </AlertDescription>
        </Alert>
      ) : null}

      <FilterBar
        active={search.outboxStatus !== 'pending'}
        onClear={() => void navigate({ search: (prev) => ({ ...prev, outboxStatus: 'pending' }) })}
        selects={[
          {
            key: 'outboxStatus',
            label: '状態',
            value: search.outboxStatus,
            allLabel: '未送出',
            options: [
              { value: 'pending', label: '未送出' },
              { value: 'failed', label: '失敗（再試行中）' },
              { value: 'dead', label: '打ち切り' },
              { value: 'sent', label: '送出済み' },
              { value: 'all', label: 'すべて' },
            ],
            onChange: (v) =>
              void navigate({
                search: (prev) => ({ ...prev, outboxStatus: (v ?? 'pending') as typeof search.outboxStatus }),
              }),
          },
        ]}
      >
        <div className="ml-auto">
          <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
        </div>
      </FilterBar>

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(o) => o.id}
        loading={list.query.isLoading}
        empty={<ListEmpty filtered={search.outboxStatus !== 'pending'} entityLabel="outbox の行" />}
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />
    </div>
  );
}

function RequeueButton({ id }: { id: string }): React.ReactElement {
  const qc = useQueryClient();
  const requeue = useMutation({
    mutationFn: () => api.post(`/jobs/outbox/${id}/requeue`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'outbox'] });
    },
  });
  return (
    <Button variant="outline" size="sm" onClick={() => requeue.mutate()} loading={requeue.isPending}>
      <RotateCcw aria-hidden />
      再投入
    </Button>
  );
}

/* ------------------------------------------------------------- ワーカー */

/**
 * ワーカープロセスの死活。
 *
 * **worker が死ぬと worker 自身の通報経路（`notify` キュー）も一緒に死ぬ。**
 * だから「死んだこと」を検知する側は worker の外に無ければならない。ここは
 * DB の `worker_heartbeats` を読むだけなので、1 本も生きていなくても答えが返る。
 */
function WorkersTab(): React.ReactElement {
  const query = useQuery({
    queryKey: qk.workers(),
    queryFn: () => api.get<WorkerHeartbeatList>('/jobs/workers'),
    refetchInterval: 15_000,
  });

  const items = query.data?.items ?? [];
  const alive = query.data?.alive ?? 0;

  const columns: Array<DataTableColumn<WorkerHeartbeatEntry>> = [
    {
      id: 'alive',
      header: '死活',
      width: 'w-24',
      cell: (w) =>
        w.alive ? (
          <Badge variant="success" size="sm">
            稼働中
          </Badge>
        ) : (
          <Badge variant="destructive" size="sm">
            応答なし
          </Badge>
        ),
    },
    {
      id: 'instance',
      header: 'インスタンス',
      cell: (w) => (
        <div className="min-w-0 space-y-0.5">
          <span className="block truncate font-mono text-sm">{w.instanceId}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {w.hostname} / pid {w.pid} / {w.role}
          </span>
        </div>
      ),
    },
    {
      id: 'queues',
      header: '担当キュー',
      hideBelow: 'lg',
      cell: (w) => <span className="text-xs text-muted-foreground">{w.queues.join(', ')}</span>,
    },
    { id: 'processed', header: '処理数', align: 'right', cell: (w) => formatCount(w.jobsProcessed) },
    {
      id: 'failed',
      header: '失敗数',
      align: 'right',
      hideBelow: 'md',
      cell: (w) =>
        w.jobsFailed > 0 ? (
          <span className="text-warning">{formatCount(w.jobsFailed)}</span>
        ) : (
          formatCount(0)
        ),
    },
    { id: 'lastJobAt', header: '最終処理', hideBelow: 'md', cell: (w) => formatRelative(w.lastJobAt) },
    { id: 'lastSeenAt', header: '最終応答', cell: (w) => formatRelative(w.lastSeenAt) },
    { id: 'startedAt', header: '起動', hideBelow: 'lg', cell: (w) => formatDateTime(w.startedAt) },
  ];

  return (
    <div className="space-y-5">
      {!query.isLoading && alive === 0 ? (
        <Alert variant="destructive">
          <AlertTitle>生存しているワーカーが 1 つもありません</AlertTitle>
          <AlertDescription>
            キューに積んだ仕事が<strong>誰にも実行されていません</strong>。
            プロビジョニング・通知メール・請求の締め処理がすべて止まります。worker コンテナを確認してください。
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          label="稼働中"
          value={formatCount(alive)}
          icon={Activity}
          tone={alive === 0 ? 'destructive' : 'success'}
        />
        <StatCard label="登録インスタンス" value={formatCount(items.length)} />
        <StatCard
          label="死活のしきい値"
          value={formatCount(query.data?.heartbeatTimeoutSec)}
          unit="秒"
          hint="この時間だけ応答が無いと「応答なし」と判定します。"
        />
      </div>

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}

      <DataTable
        columns={columns}
        rows={items}
        rowKey={(w) => w.instanceId}
        loading={query.isLoading}
        empty={<ListEmpty filtered={false} entityLabel="ワーカー" />}
      />
    </div>
  );
}

/* ------------------------------------------------------------- 定期実行 */

/**
 * Redis に実際に登録されている繰り返しジョブと、その実行台帳。
 *
 * コード上の定義ではなく **Redis の実体**を出す。cron を変えると古いキーが
 * 残り、新旧どちらの間隔でも実行され続けることがあるため、掃除が効いているかを
 * 人が確かめられる必要がある。
 */
function SchedulesTab(): React.ReactElement {
  const schedules = useQuery({
    queryKey: qk.jobSchedules(),
    queryFn: () => api.get<CountedList<JobSchedulerEntry>>('/jobs/schedules'),
    refetchInterval: 60_000,
  });

  const runs = useCursorList<ScheduledRunEntry>({
    path: '/jobs/scheduled-runs',
    keyFor: (params) => qk.scheduledRuns(params),
    filters: {},
    refetchInterval: 60_000,
  });

  const scheduleColumns: Array<DataTableColumn<JobSchedulerEntry>> = [
    {
      id: 'jobName',
      header: 'ジョブ',
      cell: (s) => (
        <div className="min-w-0 space-y-0.5">
          <span className="block truncate font-mono text-sm">{s.jobName}</span>
          <span className="block truncate text-xs text-muted-foreground">{s.queue}</span>
        </div>
      ),
    },
    {
      id: 'interval',
      header: '間隔',
      cell: (s) =>
        s.pattern ? (
          <code className="font-mono text-xs">{s.pattern}</code>
        ) : s.everyMs ? (
          formatDuration(s.everyMs)
        ) : (
          '—'
        ),
    },
    { id: 'tz', header: 'タイムゾーン', hideBelow: 'md', cell: (s) => s.tz ?? '—' },
    { id: 'nextRunAt', header: '次の実行', cell: (s) => formatDateTime(s.nextRunAt) },
    {
      id: 'key',
      header: '登録キー',
      hideBelow: 'lg',
      cell: (s) => <code className="font-mono text-[11px] text-muted-foreground">{s.key}</code>,
    },
  ];

  const runColumns: Array<DataTableColumn<ScheduledRunEntry>> = [
    { id: 'status', header: '結果', width: 'w-28', cell: (r) => <StatusBadge status={r.status} size="sm" dot /> },
    {
      id: 'jobName',
      header: 'ジョブ',
      cell: (r) => (
        <div className="min-w-0 space-y-0.5">
          <span className="block truncate font-mono text-sm">{r.jobName}</span>
          <span className="block truncate text-xs text-muted-foreground">{r.queue}</span>
        </div>
      ),
    },
    { id: 'startedAt', header: '開始', cell: (r) => formatDateTime(r.startedAt) },
    { id: 'duration', header: '所要', align: 'right', hideBelow: 'md', cell: (r) => formatDuration(r.durationMs) },
    {
      id: 'error',
      header: 'エラー',
      hideBelow: 'md',
      cell: (r) => (
        <span className="line-clamp-2 text-xs text-destructive" title={r.error ?? undefined}>
          {r.error ?? '—'}
        </span>
      ),
    },
    {
      id: 'instance',
      header: '実行インスタンス',
      hideBelow: 'lg',
      cell: (r) => <span className="font-mono text-[11px] text-muted-foreground">{r.instanceId ?? '—'}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <Section
        title="Redis の繰り返し登録"
        description="コード上の定義ではなく、実際に登録されているものを出しています。想定より多いときは古いキーが残っています。"
      >
        {schedules.isError ? (
          <QueryError error={schedules.error} onRetry={() => void schedules.refetch()} />
        ) : null}
        <DataTable
          columns={scheduleColumns}
          rows={schedules.data?.items ?? []}
          rowKey={(s) => s.key}
          loading={schedules.isLoading}
          empty={<ListEmpty filtered={false} entityLabel="繰り返し登録" />}
        />
      </Section>

      <Section
        title="定期ジョブの実行台帳"
        description="「予定時刻を過ぎても実行記録が無い」を人が確認するためのものです。"
      >
        {runs.query.isError ? (
          <QueryError error={runs.query.error} onRetry={() => void runs.query.refetch()} />
        ) : null}
        <DataTable
          columns={runColumns}
          rows={runs.rows}
          rowKey={(r) => r.id}
          loading={runs.query.isLoading}
          empty={<ListEmpty filtered={false} entityLabel="実行記録" />}
          footer={
            <CursorPagination
              nextCursor={runs.nextCursor}
              canGoPrev={runs.canGoPrev}
              onNext={runs.goNext}
              onPrev={runs.goPrev}
              limit={runs.limit}
              onLimitChange={runs.setLimit}
              currentCount={runs.rows.length}
              loading={runs.query.isFetching}
            />
          }
        />
      </Section>
    </div>
  );
}
